import type { SVGProps } from "react";
import { useCustom, useGetIdentity, useGetLocale, useTranslate } from "@refinedev/core";
import {
  PawIcon,
  CalendarIcon,
  BuildingIcon,
  PlusIcon,
  ShieldIcon,
  ChevronDownIcon,
} from "@fawkes/ui";

import { LoadingState } from "../../components/states";
import { FosterError } from "./FosterError";
import type { Locale } from "../../i18n/messages";
import type { Page } from "../../shell/nav";
import { AuthImage } from "./AuthImage";
import { PhoneIcon } from "./icons";
import { dayDelta, formatDateLong, formatDateTime, formatTime, formatWeight } from "./format";
import type {
  PortalAbsence,
  PortalAnimal,
  PortalContact,
  PortalDashboard,
  PortalOrganizationContact,
  PortalUpdate,
} from "./types";
import "./dashboard.css";

// jmix-8.1 + foster-portal redesign: Dashboard — the foster's landing screen, rebuilt to the
// "pleeggezinnen-dashboard-react" mockup (two-column card grid). Data wiring is unchanged: the federated
// animals + best-effort recent-updates feed + the foster's OWN upcoming absences + the per-org contact
// payload. Graceful degradation preserved (a backoffice outage flags `degraded` → a banner; the rest
// still renders). The sidebar/nav is the app AppShell (not rebuilt); this is the page content only.
// Styling lives in dashboard.css, scoped under `.foster-dashboard`. Icons are @fawkes/ui where they
// exist; a few (heart/sun/message/chevron-right) are inline SVGs (not in the shared set, Golden Rule).

// ── inline icons missing from @fawkes/ui ──────────────────────────────────────
function HeartIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={40} height={40} fill="currentColor" aria-hidden="true" {...p}>
      <path d="M12 21s-7.5-4.6-10-9.3C.4 8.3 2 4.6 5.4 4.6c2 0 3.4 1.1 4.6 2.7 1.2-1.6 2.6-2.7 4.6-2.7 3.4 0 5 3.7 3.4 7.1C19.5 16.4 12 21 12 21z" />
    </svg>
  );
}
function SunIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={34} height={34} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
    </svg>
  );
}
function MessageIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={26} height={26} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M21 11.5a8.4 8.4 0 0 1-11.9 7.6L3 21l1.9-6.1A8.4 8.4 0 1 1 21 11.5z" />
    </svg>
  );
}
function ChevronRightIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2.2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

function speciesLine(animal: PortalAnimal): string | null {
  if (!animal.species) {
    return null;
  }
  return animal.breed ? `${animal.species} (${animal.breed})` : animal.species;
}

/** "Vandaag, 10:00" / "Gisteren, 10:00" / "10-06-2026 10:00" — legacy DashboardView relative time. */
function relativeTime(iso: string | null, locale: Locale, t: ReturnType<typeof useTranslate>): string {
  const delta = dayDelta(iso);
  if (delta === null) {
    return "";
  }
  if (delta === 0) {
    return `${t("portal.dashboard.time.today")}, ${formatTime(iso, locale)}`;
  }
  if (delta === 1) {
    return `${t("portal.dashboard.time.yesterday")}, ${formatTime(iso, locale)}`;
  }
  return formatDateTime(iso, locale);
}

function absenceTypeKey(type: PortalAbsence["type"]): string {
  return `portal.availability.type.${type}`;
}

function formatAddress(organization: PortalOrganizationContact): string | null {
  const a = organization.address;
  if (!a) {
    return null;
  }
  const line = [a.street, [a.postalCode, a.city].filter(Boolean).join(" ")]
    .filter((p) => p && p.trim().length > 0)
    .join(", ");
  return line.length > 0 ? line : null;
}

function AnimalRow({
  animal,
  vestiging,
  locale,
  onDetails,
}: {
  animal: PortalAnimal;
  vestiging: string | null;
  locale: Locale;
  onDetails: () => void;
}) {
  const t = useTranslate();
  return (
    <article className="fd-animal-row">
      <AuthImage
        className="fd-animal-photo"
        path={
          animal.hasPhoto
            ? `/portal/organizations/${animal.organizationUuid}/animals/${animal.dierUuid}/photo`
            : null
        }
        alt={animal.name ? `${t("portal.animals.photoAltNamed")}${animal.name}` : t("portal.animals.photoAlt")}
        size="96px"
      />

      <div>
        <div className="fd-name-line">
          <h3>{animal.name || "—"}</h3>
        </div>
        {speciesLine(animal) ? (
          <div className="fd-meta-line">
            <PawIcon width={16} height={16} />
            <span>{speciesLine(animal)}</span>
          </div>
        ) : null}
        {animal.since ? (
          <div className="fd-meta-line">
            <CalendarIcon width={16} height={16} />
            <span>
              {t("portal.dashboard.since")} {formatDateLong(animal.since, locale)}
            </span>
          </div>
        ) : null}
      </div>

      <div className="fd-animal-location">
        <div className="fd-location-label">{t("portal.dashboard.vestiging")}</div>
        <div className="fd-location-value">
          <BuildingIcon width={18} height={18} />
          <span>{vestiging || "—"}</span>
        </div>
      </div>

      <button className="fd-details-button" type="button" onClick={onDetails}>
        {t("portal.dashboard.viewDetails")}
        <ChevronRightIcon />
      </button>
    </article>
  );
}

function UpdateRow({
  update,
  locale,
  animalsByDier,
}: {
  update: PortalUpdate;
  locale: Locale;
  animalsByDier: Map<string, PortalAnimal>;
}) {
  const t = useTranslate();
  const text =
    update.type === "WEIGHT"
      ? `${t("portal.dashboard.updates.weight")}: ${formatWeight(update.weightKg, locale)} ${t(
          "portal.dashboard.updates.unit.kg",
        )}`
      : (update.text ?? "—");
  const animal = animalsByDier.get(update.dierUuid);
  return (
    <article className="fd-update-row">
      <AuthImage
        className="fd-update-avatar"
        path={
          animal?.hasPhoto
            ? `/portal/organizations/${animal.organizationUuid}/animals/${update.dierUuid}/photo`
            : null
        }
        alt={update.animalName ? `${t("portal.animals.photoAltNamed")}${update.animalName}` : t("portal.animals.photoAlt")}
        size="46px"
      />
      <div className="fd-update-person">
        <div className="fd-update-name">{update.animalName ?? "—"}</div>
        <span>{relativeTime(update.at, locale, t)}</span>
      </div>
      <div className="fd-update-copy">
        <strong>{text}</strong>
        <span>
          {update.source === "person"
            ? t("portal.dashboard.updates.byPerson")
            : t("portal.dashboard.updates.byOrganization")}
        </span>
      </div>
    </article>
  );
}

function ContactBlock({ organization }: { organization: PortalOrganizationContact }) {
  const t = useTranslate();
  const address = formatAddress(organization);
  return (
    <div className="fd-contact-block">
      <strong>{organization.name || t("portal.contact.fallbackName")}</strong>
      {!organization.reachable ? (
        <span className="fd-empty">{t("portal.contact.unreachable")}</span>
      ) : (
        <>
          {organization.phone ? (
            <span>
              <PhoneIcon width={17} height={17} />
              <a href={`tel:${organization.phone}`}>{organization.phone}</a>
            </span>
          ) : null}
          {address ? (
            <span>
              <BuildingIcon width={17} height={17} />
              {address}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}

export function Dashboard({
  onNavigate,
  onOpenAnimal,
}: {
  onNavigate: (page: Page) => void;
  onOpenAnimal: (organizationUuid: string, dierUuid: string) => void;
}) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { data: identity } = useGetIdentity<{ name?: string }>();
  const { data, isLoading, isError, refetch } = useCustom<PortalDashboard>({
    url: "portal/dashboard",
    method: "get",
  });
  const { data: contactData } = useCustom<PortalContact>({
    url: "portal/contact",
    method: "get",
  });

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError || !data?.data) {
    return <FosterError onRetry={() => void refetch()} onNavigate={onNavigate} />;
  }

  const { animals, upcomingAbsences, recentUpdates, degraded } = data.data;
  const nextAbsence = upcomingAbsences[0];
  const organizations = contactData?.data?.organizations ?? [];
  const organizationNameByUuid = new Map(organizations.map((s) => [s.organizationUuid, s.name] as const));
  const animalsByDier = new Map(animals.map((a) => [a.dierUuid, a] as const));

  return (
    <div className="foster-dashboard">
      <header className="fd-topbar">
        <div>
          <h1>
            {t("portal.dashboard.welcome")}
            {identity?.name ? `, ${identity.name}` : ""} 👋
          </h1>
          <p>{t("portal.dashboard.subtitle")}</p>
        </div>
        <div className="fd-location-select" aria-hidden="true">
          <span>{t("portal.dashboard.allLocations")}</span>
          <ChevronDownIcon width={20} height={20} />
        </div>
      </header>

      {degraded ? (
        <p className="fd-banner" role="status">
          {t("portal.backofficeUnavailable")}
        </p>
      ) : null}

      <div className="fd-grid">
        <div className="fd-main">
          {/* Mijn dieren */}
          <section className="fd-card">
            <div className="fd-section-header">
              <div className="fd-section-title">
                <PawIcon width={26} height={26} />
                <h2>{t("portal.dashboard.animals.title")}</h2>
              </div>
            </div>
            {animals.length === 0 ? (
              <p className="fd-empty">{t("portal.dashboard.animals.empty")}</p>
            ) : (
              <div className="fd-animal-list">
                {animals.map((animal) => (
                  <AnimalRow
                    key={`${animal.organizationUuid}:${animal.dierUuid}`}
                    animal={animal}
                    vestiging={organizationNameByUuid.get(animal.organizationUuid) ?? null}
                    locale={locale}
                    onDetails={() => onOpenAnimal(animal.organizationUuid, animal.dierUuid)}
                  />
                ))}
              </div>
            )}
            {animals.length > 0 ? (
              <button className="fd-card-link" type="button" onClick={() => onNavigate("my-animals")}>
                {t("portal.dashboard.allAnimals")}
                <ChevronRightIcon width={22} height={22} />
              </button>
            ) : null}
          </section>

          {/* Recente updates */}
          <section className="fd-card">
            <div className="fd-section-header">
              <div className="fd-section-title">
                <MessageIcon />
                <h2>{t("portal.dashboard.updates.title")}</h2>
              </div>
              {recentUpdates.length > 0 ? (
                <button className="fd-soft-button" type="button" onClick={() => onNavigate("my-animals")}>
                  {t("portal.dashboard.allUpdates")}
                </button>
              ) : null}
            </div>
            {recentUpdates.length === 0 ? (
              <p className="fd-empty">{t("portal.dashboard.updates.empty")}</p>
            ) : (
              <div className="fd-update-list">
                {recentUpdates.map((update, i) => (
                  <UpdateRow
                    key={`${update.dierUuid}:${i}`}
                    update={update}
                    locale={locale}
                    animalsByDier={animalsByDier}
                  />
                ))}
              </div>
            )}
            <button className="fd-add-update" type="button" onClick={() => onNavigate("my-animals")}>
              <PlusIcon width={18} height={18} />
              {t("portal.dashboard.updates.add")}
            </button>
          </section>

          {/* Bedankt */}
          <section className="fd-thanks">
            <div className="fd-thanks-icon">
              <ShieldIcon width={28} height={28} />
            </div>
            <div>
              <h2>{t("portal.dashboard.thanks.title")}</h2>
              <p>{t("portal.dashboard.thanks.body")}</p>
            </div>
            <HeartIcon className="fd-thanks-heart" width={42} height={42} />
          </section>
        </div>

        <div className="fd-side">
          {/* Beschikbaarheid */}
          <section className="fd-card">
            <div className="fd-section-header">
              <div className="fd-section-title">
                <CalendarIcon width={26} height={26} />
                <h2>{t("portal.dashboard.availability.title")}</h2>
              </div>
            </div>
            <h3 className="fd-subheading">{t("portal.dashboard.availability.next")}</h3>
            {nextAbsence ? (
              <div className="fd-vacation">
                <SunIcon className="fd-sun" />
                <div>
                  <div className="fd-vacation-title">{t(absenceTypeKey(nextAbsence.type))}</div>
                  <p>
                    {formatDateLong(nextAbsence.van, locale)} {t("portal.availability.rangeSeparator")}{" "}
                    {formatDateLong(nextAbsence.tot, locale)}
                  </p>
                  <p>{t("portal.dashboard.availability.allOrganizations")}</p>
                </div>
                <span className="fd-status-pill">{t("portal.dashboard.availability.planned")}</span>
              </div>
            ) : (
              <p className="fd-empty">{t("portal.dashboard.availability.none")}</p>
            )}
            <button className="fd-outline-button" type="button" onClick={() => onNavigate("availability")}>
              <PlusIcon width={19} height={19} />
              {t("portal.dashboard.availability.add")}
            </button>
            <button className="fd-card-link" type="button" onClick={() => onNavigate("availability")}>
              {t("portal.dashboard.availability.toAvailability")}
              <ChevronRightIcon width={22} height={22} />
            </button>
          </section>

          {/* Contact & spoed */}
          <section className="fd-card">
            <div className="fd-section-header">
              <div className="fd-section-title">
                <PhoneIcon width={26} height={26} />
                <h2>{t("portal.dashboard.contact.heading")}</h2>
              </div>
            </div>
            <h3 className="fd-subheading">{t("portal.dashboard.contact.daytime")}</h3>
            {organizations.length === 0 ? (
              <p className="fd-empty">{t("portal.dashboard.contact.unavailable")}</p>
            ) : (
              <div className="fd-contact-panel">
                {organizations.map((organization) => (
                  <ContactBlock key={organization.organizationUuid} organization={organization} />
                ))}
              </div>
            )}
            <div className="fd-emergency" role="note">
              <strong>{t("portal.contact.emergency.title")}</strong>
              <span>
                <PhoneIcon width={17} height={17} />
                {t("portal.contact.emergency.number")}
              </span>
              <p>{t("portal.contact.emergency.note")}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
